import React, { useMemo } from "react";
import { Box, Card, CardContent, Typography, CircularProgress } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const formatCurrency = (value) => {
  if (value == null || value === "") return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
};

// Format YYYY-MM to short label e.g. "Jan 2025"
const formatMonthLabel = (monthStr) => {
  if (!monthStr || typeof monthStr !== "string") return monthStr;
  const [y, m] = monthStr.split("-").map(Number);
  if (!y || !m) return monthStr;
  const date = new Date(y, m - 1, 1);
  return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

/**
 * Trend chart for customer scope metric yearly_invoice_trend.
 * API returns array of { month: "YYYY-MM", total_billed: number }.
 */
const YearlyInvoiceTrendChart = ({ trendData, isLoading, avgMonthlySpend, fillHeight }) => {
  const chartData = useMemo(() => {
    if (!Array.isArray(trendData) || trendData.length === 0) return [];
    return trendData.map((d) => ({
      month: d.month,
      label: formatMonthLabel(d.month),
      total_billed: Number(d.total_billed) || 0,
    })).sort((a, b) => a.month.localeCompare(b.month));
  }, [trendData]);

  return (
    <Card
      sx={{
        borderRadius: fillHeight ? "12px" : "8px",
        border: "1px solid #e2e8f0",
        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
        height: fillHeight ? "100%" : 100,
        width: fillHeight ? "100%" : undefined,
        flex: fillHeight ? 1 : undefined,
        "&:hover": { boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.08)" },
      }}
    >
      <CardContent sx={{ p: 1.5, height: "100%", display: "flex", flexDirection: "column", "&:last-child": { pb: 1.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5, minHeight: 20 }}>
          <Typography sx={{ color: "#64748b", fontSize: "0.6875rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            Yearly Invoice Trend
          </Typography>
          {avgMonthlySpend != null && !isLoading && chartData.length > 0 && (
            <Typography sx={{ fontSize: "0.6875rem", color: "#64748b", fontWeight: 500 }}>
              Avg: {formatCurrency(avgMonthlySpend)}
            </Typography>
          )}
        </Box>
        {isLoading ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 56 }}>
            <CircularProgress size={24} sx={{ color: "#3b82f6" }} />
          </Box>
        ) : chartData.length === 0 ? (
          <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", minHeight: 56, color: "#94a3b8", fontSize: "0.75rem" }}>
            No trend data
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 56 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 4, right: 4, left: -8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: "#64748b" }} tickLine={false} axisLine={false} />
                <YAxis tickFormatter={(v) => (v >= 1000 ? `$${v / 1000}k` : `$${v}`)} tick={{ fontSize: 9, fill: "#64748b" }} width={32} tickLine={false} axisLine={false} />
                <Tooltip
                  formatter={(value) => formatCurrency(value)}
                  labelFormatter={(label) => label}
                  contentStyle={{ fontSize: "11px", padding: "6px 8px", borderRadius: "6px", border: "1px solid #e2e8f0", boxShadow: "0 2px 4px rgba(0,0,0,0.08)" }}
                />
                <Line type="monotone" dataKey="total_billed" stroke="#3b82f6" strokeWidth={1.5} dot={false} activeDot={{ r: 4 }} name="Total Billed" />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default YearlyInvoiceTrendChart;
